// Shown to signed-in users whose role is not `admin`. Field staff are
// authenticated but have no admin surface to land on; a polite dead
// end is better than redirecting them back to a login they are
// already through.
//
// W2.5 redesign: Spring Wood backdrop, BrandHeader at top, ShieldAlert
// icon at brand-deep tint, two action buttons (capture home + sign-out
// to switch user). Role string is interpolated, never hardcoded.

import { ShieldAlert } from 'lucide-react'
import { Link } from 'react-router'

import { useAuth } from './auth-provider'
import { BrandHeader } from '../ui/brand-header'
import { Button } from '../ui/button'

export interface UnauthorizedPageProps {
  // Kept loose — this page also renders before the role literal
  // type is narrowed in route guards. Exact enum values live in
  // hooks/use-me.ts and packages/shared/src/constants/user-role.ts.
  role?: string
}

export function UnauthorizedPage({ role }: UnauthorizedPageProps) {
  const { logout } = useAuth()

  return (
    <main
      role="alert"
      className="flex min-h-screen flex-col bg-[color:var(--color-surface)]"
    >
      <header className="border-b border-gray-200 bg-white px-4 py-3">
        <BrandHeader variant="topbar" />
      </header>
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="max-w-md space-y-4 text-center">
          <ShieldAlert
            aria-hidden="true"
            size={64}
            className="mx-auto text-[color:var(--color-brand-deep)]"
          />
          <h1 className="text-2xl font-semibold text-[color:var(--color-ink)]">
            Admin access required
          </h1>
          <p className="text-sm text-gray-600">
            Your account is signed in
            {role === undefined ? '' : ` as ${role}`}, but the admin console
            is only available to users with the admin role. If you need
            access, ask your Citycare administrator to update your role.
          </p>
          <div className="flex flex-col items-stretch gap-2 pt-2 sm:flex-row sm:justify-center">
            <Link to="/capture" className="contents">
              <Button variant="primary">Back to capture home</Button>
            </Link>
            <Button variant="secondary" onClick={logout}>
              Sign out and switch user
            </Button>
          </div>
        </div>
      </div>
    </main>
  )
}
